import { KeyboardEvent, useState } from "react";
import { flushSync } from "react-dom";
import { Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import useFocus from "../hooks/useFocus";
import "./EditableTitle.scss";

type EditableTitleProps = {
  value: string;
  onSave: (name: string) => Promise<unknown>;
  editable?: boolean;
};

export function EditableTitle({ value, onSave, editable = true }: EditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [inputRef, focusInput] = useFocus();

  const startEditing = () => {
    setDraft(value);
    setEditing(true);
    (focusInput as () => void)();
  };

  const finishEditing = () => {
    setEditing(false);
    setSaving(false);
  };

  const save = () => {
    const name = draft.trim();
    if (!name || name === value) {
      finishEditing();
      return;
    }
    setSaving(true);
    onSave(name).finally(finishEditing);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      flushSync(() => setDraft(value));
    }
    if (e.key === "Enter" || e.key === "Escape") {
      e.currentTarget.blur();
    }
  };

  if (!editable) {
    return <h1 className="editable-title-static">{value || "Untitled"}</h1>;
  }

  if (editing) {
    return (
      <div className="editable-title-rename" data-value={draft}>
        <Input
          ref={inputRef}
          className="editable-title-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          readOnly={saving}
        />
      </div>
    );
  }

  return (
    <div className="editable-title" onClick={startEditing}>
      <h1>{value || "Untitled"}</h1>
      <EditOutlined className="editable-title-icon" />
    </div>
  );
}
